// src/components/avatar/app-avatar.tsx
import * as React from 'react';
import { useAvatarBlob } from '../../contexts/auth/auth-context';

export type AppAvatarProps = {
  /** If true, always uses the built-in admin avatar (public/chris.jpg) */
  admin?: boolean;

  /** User id to fetch avatar blob for */
  userId?: string | null;

  /** Cache version (e.g. profile.avatar_version or profile.updated_at) */
  version?: string | number | null;

  /** Used for <img alt> and aria */
  alt: string;

  /** Fallback initials (e.g. "AB") */
  initials?: string;

  /** Built-in admin avatar path (defaults to /chris.jpg) */
  adminSrc?: string;

  /** Static image to show if the blob fetch fails (before falling back to initials) */
  fallbackSrc?: string;

  /** Standard image props */
  width?: number;
  height?: number;

  /** Custom styles for the image */
  imgStyle?: React.CSSProperties;

  /** Custom styles for the fallback */
  fallbackStyle?: React.CSSProperties;

  /** If you need to control objectFit, radius, etc. pass via imgStyle */
  className?: string;

  /** Optional click handler */
  onClick?: () => void;

  /** Pass false when the profile has no avatar_url — skips the network fetch entirely */
  hasAvatar?: boolean;
};

function safeInitials(v: unknown): string {
  const s = String(v ?? '').trim();
  if (!s) return '?';
  return s.slice(0, 2).toUpperCase();
}

export const AppAvatar: React.FC<AppAvatarProps> = React.memo(({
  admin = false,
  userId = null,
  version = null,
  alt,
  initials,
  adminSrc = '/chris.jpg',
  fallbackSrc,
  width,
  height,
  imgStyle,
  fallbackStyle,
  className,
  onClick,
  hasAvatar,
}) => {
  const [broken, setBroken] = React.useState(false);
  const [fallbackBroken, setFallbackBroken] = React.useState(false);

  // ✅ reset "broken" + retry counter if the identity/version changes
  const lastKeyRef = React.useRef<string>('');
  const retryCountRef = React.useRef(0);
  const key = `${admin ? 'admin' : 'user'}::${String(userId ?? '')}::${String(version ?? '')}`;

  React.useEffect(() => {
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      setBroken(false);
      setFallbackBroken(false);
      retryCountRef.current = 0;
    }
  }, [key]);

  const w = width ?? 29;
  const h = height ?? 29;

  const enabled = !!userId && !broken && !admin && hasAvatar !== false;

  const { getAvatarState, ensureAvatar, invalidateAvatar } = useAvatarBlob();

  const state = enabled
    ? getAvatarState({ userId: userId ?? null, version: version ?? null })
    : { src: undefined, loading: false, error: null, missing: false };

  // ✅ If not cached yet, request it (no-op if AuthProvider already preloaded it)
  // ✅ Missing (404) = never retry. Other errors (403, network) = retry up to 2× at 10s intervals.
  React.useEffect(() => {
    let cleanup: (() => void) | undefined;

    if (enabled && userId && !admin) {
      if (state.src) {
        retryCountRef.current = 0;
      } else if (!state.loading && !state.missing) {
        if (state.error) {
          if (retryCountRef.current < 2) {
            retryCountRef.current += 1;
            const timer = setTimeout(() => {
              invalidateAvatar({ userId: userId ?? null, version: version ?? null });
            }, 10_000);
            cleanup = () => clearTimeout(timer);
          }
        } else {
          void ensureAvatar({ userId, version, silent: true });
        }
      }
    }

    return cleanup;
  }, [
    enabled,
    userId,
    version,
    state.src,
    state.loading,
    state.missing,
    state.error,
    ensureAvatar,
    invalidateAvatar,
    admin,
  ]);

  if (admin) {
    return (
      <img
        src={adminSrc}
        alt={alt}
        width={w}
        height={h}
        className={className}
        style={{
          display: 'block',
          borderRadius: '50%',
          objectFit: 'cover',
          ...(imgStyle || {}),
        }}
        onClick={onClick}
        onError={() => setBroken(true)}
      />
    );
  }

  const showImg = !!state.src && !broken;

  if (!showImg) {
    // Try static fallback image before showing initials
    if (fallbackSrc && !fallbackBroken) {
      return (
        <img
          src={fallbackSrc}
          alt={alt}
          width={w}
          height={h}
          className={className}
          style={{
            display: 'block',
            borderRadius: '50%',
            objectFit: 'cover',
            ...(imgStyle || {}),
          }}
          onClick={onClick}
          onError={() => setFallbackBroken(true)}
        />
      );
    }

    return (
      <div
        aria-label={alt}
        className={className}
        onClick={onClick}
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          width: w,
          height: h,
          borderRadius: '50%',
          background: '#2B2D33',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          userSelect: 'none',
          ...(fallbackStyle || {}),
        }}
      >
        {safeInitials(initials)}
      </div>
    );
  }

  return (
    <img
      src={state.src}
      alt={alt}
      width={w}
      height={h}
      className={className}
      style={{
        display: 'block',
        borderRadius: '50%',
        objectFit: 'cover',
        ...(imgStyle || {}),
      }}
      onClick={onClick}
      onError={() => {
        setBroken(true);
        invalidateAvatar({ userId: userId ?? null, version: version ?? null });
      }}
    />
  );
});

AppAvatar.displayName = 'AppAvatar';
